import React, { useState } from "react";
import heroImg from "../assets/images/heroimg.png";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import CreateCompanyFooter from "../components/createcompanyfooter/CreateCompanyFooter";
import { Button, Modal } from "react-bootstrap";
import { getApiBaseUrl } from "../apiBaseUrlFinder";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

const MESSAGE_LIMIT = 1000;

const ContactUs = () => {
  const validationSchema = Yup.object().shape({
    // Full Name: OPTIONAL
    name: Yup.string().trim().notRequired(),

    organization: Yup.string(), // Optional field

    // Email: OPTIONAL, but must be valid if provided
    email: Yup.string()
      .trim()
      .transform((v) => (v === "" ? undefined : v))
      .email("Invalid email format.")
      .notRequired(),

    // Phone: OPTIONAL (still validate if provided)
    phone: Yup.string()
      .transform((v) => (v === "" ? undefined : v))
      .matches(/^[0-9+\-\s]+$/, "Invalid phone number")
      .notRequired(),

    // Required fields
    title: Yup.string().required("Title is required."),
    message: Yup.string()
      .required("Please provide details.")
      .max(MESSAGE_LIMIT, `Message must be at most ${MESSAGE_LIMIT} characters.`),
  });

  // Initial Values
  const initialValues = {
    name: "",
    organization: "",
    email: "",
    phone: "",
    title: "",
    message: "",
  };

  const [showModal, setShowModal] = useState(false);

  const handleSubmit = async (values, { resetForm }) => {
    const payload = {
      full_name: values.name || undefined,
      organization: values.organization || undefined,
      email_address: values.email || undefined,
      phone_number: values.phone || undefined,
      title: values.title,
      description: values.message,
    };

    try {
      const response = await fetch(`${getApiBaseUrl()}/contact_us`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error?.message || "Something went wrong!");
      }

      resetForm();
      setShowModal(true);
    } catch (error) {
      console.error("Form submission error:", error.message);
      alert(`Error: ${error.message}`);
    }
  };

  const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const location = useLocation();

  return (
    <div className="px-lg-2 px-md-2 px-2">
      <div className="container">
        <div className="row">
          <div className="co-lg-12">
            <div className="pt-5">
              <div className="d-flex justify-content-center">
                <img src={heroImg} alt="Hero" className="img-fluid" style={{ width: "250px" }} />
              </div>
            </div>
          </div>
        </div>

        <div className="row pt-lg-5 pt-md-4 pt-3 mt-lg-0 mt-md-3 mt-3">
          <div className="offset-lg-3 col-lg-6">
            <div className="get_updates_box">
              <h4 className="contact_us_h4 pt-4 text-center">
                Reach out to us regarding bugs, feature requests, business inquiries, etc.
              </h4>

              <div className="row">
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                  {({ handleSubmit, errors, touched, values, setFieldValue }) => {
                    const remaining = MESSAGE_LIMIT - (values.message?.length || 0);

                    return (
                      <Form onSubmit={handleSubmit}>
                        <div className="row pt-4 pb-4">
                          <div className="col-lg-12">
                            {/* Full Name (Optional) */}
                            <div className="mb-3">
                              <Field
                                type="text"
                                id="name"
                                name="name"
                                className={`form-control create_company_input ${
                                  errors.name && touched.name ? "error" : ""
                                }`}
                                placeholder="Full Name (Optional)"
                              />
                              <ErrorMessage name="name" component="small" className="text-danger" />
                            </div>

                            {/* Organization (Optional) */}
                            <div className="mb-3">
                              <Field
                                type="text"
                                id="organization"
                                name="organization"
                                className={`form-control create_company_input ${
                                  errors.organization && touched.organization ? "error" : ""
                                }`}
                                placeholder="Your Organization (Optional)"
                              />
                            </div>

                            {/* Email (Optional) */}
                            <div className="mb-3">
                              <Field
                                type="email"
                                id="email"
                                name="email"
                                className={`form-control create_company_input ${
                                  errors.email && touched.email ? "error" : ""
                                }`}
                                placeholder="Email Address (Optional)"
                              />
                              <ErrorMessage name="email" component="small" className="text-danger" />
                            </div>

                            {/* Phone Number (Optional) */}
                            <div className="mb-3">
                              <Field
                                type="tel"
                                id="phone"
                                name="phone"
                                className={`form-control create_company_input ${
                                  errors.phone && touched.phone ? "error" : ""
                                }`}
                                placeholder="Phone Number (Optional)"
                              />
                              <ErrorMessage name="phone" component="small" className="text-danger" />
                            </div>

                            {/* Title (Required) */}
                            <div className="mb-3">
                              <Field
                                as="select"
                                id="title"
                                name="title"
                                className={`form-control create_company_input ${
                                  errors.title && touched.title ? "error" : ""
                                }`}
                              >
                                <option value="" disabled>
                                  Select an option
                                </option>
                                <option value="Bug">Bug</option>
                                <option value="New Feature">New Feature</option>
                                <option value="Business Inquiry">Business Inquiry</option>
                                <option value="Other">Other</option>
                              </Field>
                              <ErrorMessage name="title" component="small" className="text-danger" />
                            </div>

                            {/* Message with counter (Required) */}
                            <div className="mb-3" style={{ position: "relative" }}>
                              <Field name="message">
                                {({ field }) => (
                                  <textarea
                                    id="message"
                                    rows="5"
                                    className={`form-control create_company_textarea ${
                                      errors.message && touched.message ? "error" : ""
                                    }`}
                                    placeholder="Tell us how may we help you!"
                                    maxLength={MESSAGE_LIMIT}
                                    value={field.value}
                                    onChange={(e) => {
                                      const val = e.target.value || "";
                                      const next = val.length > MESSAGE_LIMIT ? val.slice(0, MESSAGE_LIMIT) : val;
                                      setFieldValue("message", next);
                                    }}
                                    onBlur={field.onBlur}
                                    name={field.name}
                                  />
                                )}
                              </Field>

                              <ErrorMessage name="message" component="small" className="text-danger d-block mt-1" />
                            </div>

                            {/* Counter outside box */}
                            <div
                              style={{
                                textAlign: "right",
                                fontSize: 12,
                                color: "#6c757d",
                                marginTop: "-15px",
                                marginBottom: "10px",
                              }}
                            >
                              {values.message?.length
                                ? `${remaining} characters left`
                                : `${MESSAGE_LIMIT} characters left`}
                            </div>

                            {/* Submit Button */}
                            <div className="text-center">
                              <button type="submit" className="btn submit_contact_form_btn mt-lg-3">
                                Submit
                              </button>
                            </div>
                          </div>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>

                {/* Success Modal */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static">
                  <Modal.Header className="border-0">
                    <Modal.Title className="fw-bold text-success">🎉 Submission Successful!</Modal.Title>
                  </Modal.Header>
                  <Modal.Body className="text-center">
                    <div className="d-flex justify-content-center mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center bg-success bg-gradient text-white rounded-circle shadow-lg"
                        style={{ width: "80px", height: "80px" }}
                      >
                        <i className="bi bi-check-lg" style={{ fontSize: "2rem" }}></i>
                      </div>
                    </div>
                    <h5 className="text-secondary">Thank you for reaching out to us.</h5>
                    <p className="text-muted">
                      We have received your message and will get back to you shortly.
                    </p>
                  </Modal.Body>
                  <Modal.Footer className="border-0 d-flex justify-content-center">
                    <Button variant="success" onClick={() => setShowModal(false)} className="px-4 py-2 fw-semibold shadow-sm">
                      Close
                    </Button>
                  </Modal.Footer>
                </Modal>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CreateCompanyFooter />
    </div>
  );
};

export default ContactUs;